import { api } from "./client";

// Payment System v3.0 PR 11 — read-only ledger views (design spec §10.7).

export type LedgerStatus = "Live" | "Instructed" | "Reversed" | "Adjusted";

export interface LedgerFilterQuery {
  from?: string;
  to?: string;
  status?: LedgerStatus;
  paymentType?: string;
}

// --- Loan Activity ----------------------------------------------------------

export interface LoanActivityLedgerRow {
  disbursementId: string;
  paymentId: string;
  date: string;
  paymentType: string;
  waterfallNumber: number;
  priorityPosition: number;
  obligationTypeCode: string;
  obligationTypeDisplay: string | null;
  recipient: string | null;
  amount: number;
  balanceAfter: number | null;
  status: LedgerStatus;
}

export interface LoanActivityLedger {
  loanId: string;
  loanNumber: string | null;
  totalLive: number;
  totalReversed: number;
  totalAdjusted: number;
  rows: LoanActivityLedgerRow[];
}

// --- Pool -------------------------------------------------------------------

export interface PoolLedgerRow {
  disbursementId: string;
  paymentId: string;
  date: string;
  paymentType: string;
  priorityPosition: number;
  obligationTypeCode: string;
  obligationTypeDisplay: string | null;
  recipient: string | null;
  amount: number;
  status: LedgerStatus;
  loanId: string | null;
}

export interface PoolLedgerSection {
  sectionName: string;
  priorityFrom: number;
  priorityTo: number;
  total: number;
  rows: PoolLedgerRow[];
}

export interface PoolReconciliation {
  poolLedgerTotal: number;
  contributingLoansWf2Total: number;
  variance: number;
}

export interface PoolLedger {
  poolId: string;
  poolName: string | null;
  ocRatio: number;
  ocTriggerActive: boolean;
  reconciliation: PoolReconciliation;
  sections: PoolLedgerSection[];
}

// --- Investor ---------------------------------------------------------------

export interface InvestorCommitmentLedgerRow {
  commitmentId: string;
  poolId: string;
  poolName: string | null;
  tier: string | null;
  committedAmount: number;
  fundedAmount: number;
  currentPrincipalBalance: number;
  commitmentDate: string;
  status: string;
}

export interface InvestorDistributionLedgerRow {
  distributionId: string;
  commitmentId: string;
  periodStart: string;
  periodEnd: string;
  grossAmount: number;
  withheldAmount: number;
  netAmount: number;
  paidAt: string | null;
}

export interface InvestorLedger {
  investorId: string;
  investorDisplayName: string | null;
  totalCommitted: number;
  totalFunded: number;
  totalOutstanding: number;
  ytdDistributionsNet: number;
  commitments: InvestorCommitmentLedgerRow[];
  distributions: InvestorDistributionLedgerRow[];
}

// --- Tier -------------------------------------------------------------------

export interface TierSubscriberRow {
  commitmentId: string;
  investorId: string;
  investorDisplayName: string | null;
  faceAmount: number;
  currentOutstanding: number;
  sharePct: number;
}

export interface TierDistributionAllocation {
  commitmentId: string;
  investorId: string;
  investorDisplayName: string | null;
  faceAmount: number;
  sharePct: number;
  amount: number;
}

export interface TierDistributionRow {
  periodStart: string;
  periodEnd: string;
  tierTotal: number;
  allocations: TierDistributionAllocation[];
}

export interface TierLedger {
  poolId: string;
  poolName: string | null;
  tier: string;
  totalTierFace: number;
  totalOutstanding: number;
  ytdDistributionsNet: number;
  subscribers: TierSubscriberRow[];
  distributions: TierDistributionRow[];
}

// --- Transport --------------------------------------------------------------

export const ledgersApi = {
  loanActivity: (loanId: string, filter?: LedgerFilterQuery) =>
    api.get<LoanActivityLedger>(`/ledgers/loans/${loanId}/activity`, filter),
  pool: (poolId: string, filter?: LedgerFilterQuery) =>
    api.get<PoolLedger>(`/ledgers/pools/${poolId}`, filter),
  investor: (investorId: string, filter?: LedgerFilterQuery) =>
    api.get<InvestorLedger>(`/ledgers/investors/${investorId}`, filter),
  tier: (poolId: string, tier: string, filter?: LedgerFilterQuery) =>
    api.get<TierLedger>(`/ledgers/pools/${poolId}/tiers/${tier}`, filter),
};
